import {ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ComponentType} from "../../_models/component-type";
import {forkJoin, of, Subscription, Observable} from "rxjs";
import {
    Component as WireComponent,
    COMPONENT_BASE_TYPES,
    COMPONENT_BASE_TYPE_ARRAY,
    isComponentArray, isComponent
} from "../../_models/component";
import {ApiService} from "../../services/api/api.service";
import {catchError, tap, concatMap, map, take} from "rxjs/operators";
import {ListResponse, SingleResponse} from "../../services/api/response-types";
import {CustomEventsService} from '../../services/custom-events.service';
import {ConstantProperty} from '../../_models/constant-property';
import {ClearConstantService} from "../constant-field/clear-constant.service";
import {
    ConstantValue,
    ComponentConstantField,
    GenericConstantApiResponse,
    GenericField, isConstant, isFile, FileField, ConstantField, isConstantValue
} from "../../_models/api/generic-constant";
import {GenericConstantDataService} from "../../data/generic-constant-data.service";
import {IDMap, KeyMap, ModelID} from '../../_typing/generic-types';
import {ConfigColumn} from "../../_typing/config/config-column";
import {NotificationService} from "../../services/notification.service";
import {IDateTimePeriod} from "../../_typing/date-time-period";
import {HandleError, HttpErrorHandler} from "../../services/http-error-handler.service";
import {getAttributeIds} from "../../tables/custom-events-table/utils";
import {UserService} from '../../services/user.service';
import {ComponentEventsTableService} from "../../tables/component-events-table/component-events-table.service";
import {ComponentFormConfig} from "../../_typing/config/component-form-config";
import {ComponentNameFormulaBuilderService} from "../../services/component-name-formula-builder.service";
import {deepCopy, refreshSubscription} from "../../lib/utils";
import {EXTENDED_NOTIFICATION_DURATION_MS} from "../../shared/globals";
import {DateTimeInstanceService} from "../../services/date-time-instance.service";
import {FormulaPart, NameFormula} from "../../_typing/config/component-name-formula-config";
import {AppScope} from "../../services/app_scope.service";
import {GenericComponentFieldComponent} from "../generic-component-field/generic-component-field.component";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {DateTimePickerComponent} from "../../date-time/date-time-picker/date-time-picker.component";
import {ComponentTypeDataService, UniqueComponentTypeRelationshipDict} from "../../data/component-type-data.service";
import {
    ComponentComponentDataResponse,
    ComponentDataService,
    SingleComponentComponentDataResponse
} from "../../data/component-data.service";
import {RelMappingResult} from "../../_typing/relationship-api-mapping";
import {DefaultValueService} from "../default-value-field/default-value.service";
import {ConfigStub} from "../../_typing/config-stub";
import {SelectSearchComponentComponent} from "../select-search-api/single/select-search-component.component";
import {SelectManySearchBaseComponent} from "../select-search-api/many/select-many-search-base.component";

interface ComponentSaveResult {
    component: WireComponent;
    linkFailures: number;
}

@Component({
    selector: 'component-form',
    templateUrl: './component-form.component.html',
    styleUrls: ['./component-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        SharedModule,
        GenericComponentFieldComponent,
        DateTimePickerComponent,
        SelectSearchComponentComponent,
        SelectManySearchBaseComponent
    ],
    providers: [ClearConstantService]
})
export class ComponentFormComponent implements OnInit {
    @Input() config: ComponentFormConfig;
    @Output() component_saved: EventEmitter<{ component: WireComponent; custom_constant_dict: KeyMap<ConstantValue> }> =
        new EventEmitter<{ component: WireComponent, custom_constant_dict: KeyMap<ConstantValue> }>();
    @Output() component_cleared: EventEmitter<void> = new EventEmitter<void>();
    private readonly destroyRef = inject(DestroyRef);
    private readonly handleError: HandleError;

    component_type: ComponentType;
    $component_type: Subscription;
    constant_properties: ConstantProperty[];
    cpDict: Record<string, ConstantProperty> = {};
    custom_constant_dict: KeyMap<ConstantValue> = {};
    generic_field_dict: KeyMap<GenericField> = {};

    name: string;
    components: WireComponent[] = [];
    component: WireComponent;
    prop_ids: string[];
    dtp: IDateTimePeriod;

    api_base: COMPONENT_BASE_TYPES = 'component';
    base_types = COMPONENT_BASE_TYPE_ARRAY;
    name_column: ConfigColumn;
    start_column: ConfigColumn;
    end_column: ConfigColumn;
    editable_cols: ConfigColumn[];
    disabled_cols: ConfigColumn[];
    show_editable: boolean = true;
    show_disabled: boolean = false;
    missing: KeyMap<string> = {};

    autoName = false;
    nameFormula: FormulaPart[] = [];
    nameFormulaString: string;
    defaultValueDict: KeyMap<ConstantValue> = {};
    defaultComponentValueDict: KeyMap<ConfigStub<WireComponent> | ConfigStub<WireComponent>[] | null> = {};
    nameFormulaOptions: KeyMap<NameFormula> = {};
    originalSelectedComponentDict: KeyMap<ConfigStub<WireComponent> | ConfigStub<WireComponent>[] | null> = {};
    componentComponentTypeMap: SingleComponentComponentDataResponse = {filters: {}, selectedFilters: {}};
    componentComponentDict: IDMap<WireComponent>;
    newSelectedComponentDict: IDMap<WireComponent[]> = {};
    relatedCtIds: ModelID[];
    ctCtDict: IDMap<{ isFirstComponent: boolean; isUnique: boolean; isListUnique: boolean }> = {};

    missingPermissions: string[] = [];
    canSaveComponent: boolean = false;
    canSaveConstants: boolean = false;
    canSaveLinks: boolean = false;

    constructor(private api: ApiService,
                private notification: NotificationService,
                private dateInst: DateTimeInstanceService,
                private eventsService: CustomEventsService,
                private clearConstantService: ClearConstantService,
                private constantDataService: GenericConstantDataService,
                private componentTypeData: ComponentTypeDataService,
                private componentData: ComponentDataService,
                private changeDetectorRef: ChangeDetectorRef,
                private userService: UserService,
                private componentEventsService: ComponentEventsTableService,
                private nameBuilder: ComponentNameFormulaBuilderService,
                private customEventsService: CustomEventsService,
                httpErrorHandler: HttpErrorHandler,
                private defaultValueService: DefaultValueService,
                private appScope: AppScope) {
        this.handleError = httpErrorHandler.createHandleError('ComponentForm');
    }

    ngOnInit(): void {
        if (!this.config || !this.config.selected_component_type) {
            this.notification.openError("Please select a Component Type for this form.", 10000);
            return;
        }

        this.dateInst.dateTimePeriodInitialised$.pipe(take(1)).subscribe(() => {
            this.dtp = this.dateInst.dtp;
            this.setUpForm();
        })
    }

    setUpForm() {
        this.api_base = this.config.selected_component_type.attributes.base_type as COMPONENT_BASE_TYPES;
        this.name = this.config.selected_component_type.attributes.name;
        this.components = [];
        this.name_column = this.config.selected_cols.find(c => c.id === 'name');
        this.start_column = this.config.selected_cols.find(c => c.id === 'start_time');
        this.end_column = this.config.selected_cols.find(c => c.id === 'end_time');
        this.prop_ids = this.config.selected_cols.map(item => {
            if (!this.component?.id || !item.disabled) {
                return item.id;
            }
        }).filter(item => !['name', 'start_time', 'end_time'].includes(item));
        this._setDefaultValueDict();

        // Get full component_type from config id
        let $component_type_obs = this.api.component_type.getById(this.config.selected_component_type.id)
            .pipe(tap(result => {
                    this.component_type = result.data;
                })
            );
        this.relatedCtIds = this.config.selected_cols.filter(c => c.type === 'component_type')?.map(ct => ct.id);
        let $relatedComponentTypeObs = this.getComponentTypeComponentTypes();

        let $properties_obs = this.eventsService.getComponentTypeProperties(
            [this.config.selected_component_type.id], this.prop_ids)
            .pipe(tap((result: ListResponse<ConstantProperty>) => {
                    this.constant_properties = result.data;
                    this.constant_properties.forEach(cp => this.cpDict[cp.id] = cp);
                })
            );

        this.$component_type = refreshSubscription(this.$component_type);
        this.$component_type = forkJoin([$component_type_obs, $properties_obs, $relatedComponentTypeObs]).pipe(take(1), takeUntilDestroyed(this.destroyRef),
            concatMap(() => {
                this.resetNameFormula();
                if (this.config.component) {
                    this.component = this.config.component;
                    this.custom_constant_dict = {};
                    return forkJoin([this.getCustomConstants(), this.getLinkedComponents(this.component.id)]);
                } else {
                    this.clearForm();
                    this.setupFileConstants();
                    return of(null);
                }
            }))
            .subscribe(() => {
                this.groupColumns();
                this.computeMissingPermissions();
            });
    }

    getComponentTypeComponentTypes(): Observable<UniqueComponentTypeRelationshipDict> {
        if (!this.relatedCtIds?.length) return of(null);
        return this.componentTypeData.getComponentTypeComponentTypeRelationships(this.config.selected_component_type.id, this.relatedCtIds)
            .pipe(tap(result => {
                this.ctCtDict = result;
                this.relatedCtIds.forEach(ctId => {
                    this._getComponentTypeComponentFilters(ctId);
                })
            }))
    }

    private _getComponentTypeComponentFilters(relatedCtId: ModelID) {
        this.componentComponentTypeMap ??= {filters: {}, selectedFilters: {}};
        this.componentComponentTypeMap.filters ??= {};
        this.componentComponentTypeMap.selectedFilters ??= {};
        this.componentComponentTypeMap.selectedFilters[relatedCtId] = this.component?.id ? this.componentData.generateComponentsByComponentIdAndTypeFilters(this.component.id, relatedCtId) : null;
        let dateFilters: any;
        if (this.config.component_type_date_filters?.constant_property_time?.[relatedCtId]?.enabled || this.config.component_type_date_filters?.attribute_time?.[relatedCtId]?.enabled) {
            dateFilters = this.componentData.generateDateFilters(this.config.component_type_date_filters, relatedCtId, this.dtp);
        }
        this.componentComponentTypeMap.filters[relatedCtId] = this.componentData.getComponentTypeComponentFilters(this.config.selected_component_type.id, relatedCtId, this.component?.id, this.ctCtDict, dateFilters);
    }

    getNameFormulaString(): string {
        this.nameFormulaString = this.nameBuilder.getNameString(this.nameFormula, this.cpDict);
        return this.nameFormulaString;
    }

    private resetNameFormula() {
        this.nameFormulaOptions = this.component_type.attributes.json?.name_formulas || {};
        let selectedFormula = this.nameFormulaOptions[this.config.selected_name_formula?.key] || this.config.selected_name_formula?.formula;
        if (selectedFormula && !this.config.component) {
            this.autoName = true;
            this.nameFormula = this.nameBuilder.getInitialFormula(deepCopy(selectedFormula), this.defaultValueDict);
            this.getNameFormulaString();
        }
    }

    private groupColumns(): void {
        /**On side panel for new components, all properties should be editable by default**/
        const isDisabled = c => {
            return (c.disabled && this.component?.id) || (c.disabled && c.default_value) ||
                this.generic_field_dict[c.id]?.locked || this.cpDict[c.id]?.attributes.is_calculation
        }
        this.editable_cols = this.config.selected_cols.filter(c => !isDisabled(c));
        this.disabled_cols = this.config.selected_cols.filter(c => isDisabled(c));
    }

    private getCustomConstants(): Observable<GenericConstantApiResponse> {
        const atts = getAttributeIds(this.config.selected_cols);
        return this.constantDataService.getComponentConstants([this.component.id], this.prop_ids, atts).pipe(
            tap((result: GenericConstantApiResponse) => {
                this.generic_field_dict = result.data?.[this.component.id];
                Object.keys(result.data?.[this.component.id])?.forEach(key => {
                    const item = result.data?.[this.component.id][key];
                    if (isConstant(item)) {
                        this.custom_constant_dict[key] = item.value;
                    }
                    if (isFile(item)) {
                        this.custom_constant_dict[key] = item.filename;
                    }
                });
            })
        );
    }

    private getLinkedComponents(componentId: ModelID) {
        if (!this.relatedCtIds?.length) return of(null);
        return this.componentData.getComponentComponentData([componentId], this.relatedCtIds, this.ctCtDict,
            this.component_type.id, null, null).pipe(
            tap((componentData: ComponentComponentDataResponse) => {
                this.componentComponentTypeMap = Object.assign({}, this.componentComponentTypeMap || {}, {componentComponents: componentData.componentComponents[componentId]});
                this.componentComponentTypeMap = Object.assign({}, this.componentComponentTypeMap || {}, {components: componentData.components[componentId]});
                this.componentComponentDict = deepCopy(this.componentComponentTypeMap.components);
                this.changeDetectorRef.markForCheck();
            })
        )

    }

    private setupFileConstants(): void {
        this.config.selected_cols.filter(c => this.cpDict[c.id]).forEach(c => {
            if (this.cpDict[c.id]?.attributes.data_type === 'file') {
                this.generic_field_dict[c.id] = new FileField();
            } else {
                this.generic_field_dict[c.id] = new ConstantField();
                this.generic_field_dict[c.id].data_type = this.cpDict[c.id]?.attributes.data_type;
            }
        })
    }

    updateComponentComponentRelationship(componentTypeId: ModelID, $event) {
        if (Array.isArray($event)) {
            this.newSelectedComponentDict[componentTypeId] = $event || [];
        } else {
            this.newSelectedComponentDict[componentTypeId] = $event?.value ? [$event.value] : [];
        }
    }

    changeComplete(event: ConstantValue, property_id: ModelID) {
        if (this.cpDict[property_id].attributes.data_type === 'string' && event) {
            this.custom_constant_dict[property_id] = event.toString().trimRight();
        } else {
            this.custom_constant_dict[property_id] = event;
        }
        if (this.autoName) {
            this.nameFormula = this.nameBuilder.updateValue(this.nameFormula, event, property_id);
            this.getNameFormulaString();
        }
        this.changeDetectorRef.markForCheck();
    }

    save() {
        if (this.isSaveBlocked()) {
            return;
        }
        if (!this.validate()) {
            return;
        }
        if (this.autoName) {
            this.component.attributes.name = this.nameFormulaString;
        }
        let $sav_obs;
        this.component.attributes.name = this.component.attributes.name.trim();
        if (!this.component.id) {
            $sav_obs = this.api[this.component.attributes.base_type].obsSave(this.component);
        } else {
            $sav_obs = this.api[this.component.attributes.base_type].obsPatch(this.component);
        }
        $sav_obs.pipe(
            concatMap((component: SingleResponse<WireComponent>) => {
                const component_id = component.data?.id;
                let component_constants = [];

                Object.keys(this.custom_constant_dict).forEach(prop_id => {
                    if ((this.custom_constant_dict[prop_id] || this.custom_constant_dict[prop_id] === 0) &&
                        this.cpDict[prop_id]?.attributes.data_type === 'user' && this.custom_constant_dict[prop_id] === 'currentUser') {
                        this.custom_constant_dict[prop_id] = this.appScope.current_user.id;
                    }
                    if ((this.custom_constant_dict[prop_id] || this.custom_constant_dict[prop_id] === 0) &&
                        this.cpDict[prop_id]?.attributes.data_type === 'account' &&
                        this.custom_constant_dict[prop_id] === 'currentAccount') {
                        this.custom_constant_dict[prop_id] = this.appScope.active_account_id;
                    }
                    if ((this.custom_constant_dict[prop_id] || this.custom_constant_dict[prop_id] === 0) &&
                        this.cpDict[prop_id]?.attributes.data_type !== 'file') {
                        component_constants.push(new ComponentConstantField(component_id, prop_id, this.custom_constant_dict[prop_id]));
                    }
                })
                // Permission gates: when canSaveConstants/canSaveLinks is false, the corresponding fields
                // were disabled and isSaveBlocked() also blocked save for any required/defaulted column,
                // so anything reaching here is unchanged from the loaded values — safe to skip the write.
                const $constants = (this.canSaveConstants && component_constants?.length)
                    ? this.constantDataService.saveComponentConstants(component_constants).pipe(map(() => component.data))
                    : of(component.data);

                const $linkedComponents: Observable<ComponentSaveResult> = this.canSaveLinks
                    ? this.saveLinkedComponents(component.data)
                    : of({component: component.data, linkFailures: 0});

                return $constants.pipe(concatMap(() => $linkedComponents));
            }),
            tap((result: ComponentSaveResult) => {
                const component = result.component;
                this.component_saved.next({component: component, custom_constant_dict: this.custom_constant_dict});

                this.componentEventsService.$componentSaved.next({
                    component: component,
                    custom_constant_dict: this.custom_constant_dict
                });
                this.component = component;
                this.computeMissingPermissions();
                this.notifySaveOutcome(result.linkFailures);
            }),
            takeUntilDestroyed(this.destroyRef),
            take(1),
            catchError(this.handleError<ComponentSaveResult>('Save component'))
        ).subscribe();
    }

    saveLinkedComponents(component: WireComponent): Observable<ComponentSaveResult> {
        if (!this.relatedCtIds.length) return of({component, linkFailures: 0});

        const componentId = component?.id;
        const changedCtIds = this.relatedCtIds.filter(ctId => this.newSelectedComponentDict.hasOwnProperty(ctId));
        if (!changedCtIds.length) return of({component, linkFailures: 0});

        const $saveObs = changedCtIds.map((componentTypeId) => {
            const newSelectedValues = this.newSelectedComponentDict[componentTypeId] || [];
            const oldComponentComponents = this.componentComponentTypeMap.componentComponents;
            const oldComponents = this.componentComponentTypeMap.components;
            return this.componentData.saveLinkedComponents(newSelectedValues, oldComponentComponents, oldComponents, componentId, componentTypeId, this.ctCtDict);
        })
        // Carry the failure count up so save() can report successes and failures in a single snackbar,
        // rather than opening a competing error snackbar here that the success snackbar would replace.
        return forkJoin($saveObs).pipe(
            map((results: RelMappingResult[]) => results.reduce((n, r) => n + r.failures.length, 0)),
            concatMap((linkFailures: number) =>
                this.getLinkedComponents(component.id).pipe(map(() => ({component, linkFailures}))))
        );
    }

    private notifySaveOutcome(linkFailures: number): void {
        const ctName = this.component_type.attributes.name;
        const successMsg = `Component saved successfully. Please clear the form before creating a new ${ctName}.`;
        if (linkFailures > 0) {
            const failureMsg = `${linkFailures} linked component change(s) failed to save. You may not have permission to modify these links.`;
            // Passing an action forces duration:null in NotificationService, so this single combined
            // snackbar persists until the user dismisses it (the message renders via innerHTML, so <br> works).
            this.notification.openWarning(`${successMsg}<br>${failureMsg}`, undefined, 'Dismiss');
        } else {
            this.notification.openSuccess(successMsg);
        }
    }

    validate(): boolean {
        let allow_save = true;
        this.missing = {};
        this.config.selected_cols.filter(c => this.isRequired(c)).forEach(c => {
            // For required link columns, a loaded existing relationship satisfies the requirement
            // ONLY when the user hasn't touched the field. _hasExistingValue() reads
            // componentComponentDict, which holds the stale loaded value until saveLinkedComponents()
            // refreshes it post-save — so a cleared field would otherwise look "still populated".
            // Use newSelectedComponentDict.hasOwnProperty(c.id) as the "touched" signal.
            const linkTouched = c.type === 'component_type'
                && Object.prototype.hasOwnProperty.call(this.newSelectedComponentDict, c.id);
            const missingLink = c.type === 'component_type'
                && (linkTouched
                    ? !this.newSelectedComponentDict[c.id]?.length
                    : !(this.isEditing && this._hasExistingValue(c)));
            if (((c.type === 'constant_property' || this.cpDict[c.id]) && !this.custom_constant_dict[c.id] && this.custom_constant_dict[c.id] !== 0) ||
                (c.id === 'start_time' && !this.component.attributes.start_time) ||
                (c.id === 'end_time' && !this.component.attributes.end_time) ||
                missingLink) {
                this.missing[c.id] = c.title || c.column_name || c.name;
                allow_save = false;
            }
        })
        if (!allow_save) {
            this.notification.openError("The following required items are missing: " + Object.values(this.missing).join(", "), EXTENDED_NOTIFICATION_DURATION_MS);
        }
        if (!this.customEventsService.checkStartAndEndDates(this.component, 'start_time', 'end_time')) {
            allow_save = false;
        }
        return allow_save;
    }

    isRequired(c: ConfigColumn) {
        const isCalculation = this.cpDict[c.id]?.attributes?.is_calculation;
        const isNew = !this.component?.id;
        return c.required && !isCalculation && (!c.disabled || isNew)
    }

    isNameComplete(): boolean {
        return this.autoName && this.nameFormula.filter(f => !f.value).length === 0;
    }

    revalidateField($event, field: ConfigColumn) {
        this.missing[field.id] = $event ? null : (field.title || field.column_name || this.cpDict[field.id]?.attributes.name);
    }

    clearForm() {
        this.component = new WireComponent(this.api_base as COMPONENT_BASE_TYPES);
        this.custom_constant_dict = {};
        this.component.relationships.component_type = {data: {id: this.component_type.id, type: 'component_type'}};
        this.constant_properties.forEach((cp: ConstantProperty) => {
            this.custom_constant_dict[cp.id] = this.defaultValueDict[cp.id];
        });
        this.clearConstantService.clearConstants();
        this.config.component = undefined;
        this.clearComponentTypeValues();

        this.resetNameFormula();
        this.component_cleared.next();
        this.missing = {};
        this.computeMissingPermissions();
    }

    clearComponentTypeValues() {
        this.componentComponentTypeMap = {filters: {}, selectedFilters: {}};
        this.componentComponentDict = undefined;
        this.clearConstantService.clearSelected();

        this._resetComponents();
    }

    private _resetComponents() {
        this.relatedCtIds.forEach(ctId => {
            this._getComponentTypeComponentFilters(ctId);
            if (this.ctCtDict?.[ctId]?.isUnique) {
                this.originalSelectedComponentDict[ctId] = Object.assign({}, this.defaultComponentValueDict[ctId]);
                this.newSelectedComponentDict[ctId] = isComponent(this.originalSelectedComponentDict[ctId]) ? [deepCopy(this.originalSelectedComponentDict[ctId])] : [];
            } else {
                this.originalSelectedComponentDict[ctId] = isComponentArray(this.defaultComponentValueDict[ctId]) ? [...this.defaultComponentValueDict[ctId]] : [];
                this.newSelectedComponentDict[ctId] = isComponentArray(this.defaultComponentValueDict[ctId]) ? [...this.defaultComponentValueDict[ctId]] : [];
            }
        })
    }

    private _setDefaultValueDict(): void {
        this.config.selected_cols.forEach(col => {
            if (col.type === 'component_type' && col.default_value && (isComponent(col.default_value) || isComponentArray(col.default_value))) {
                this.defaultComponentValueDict[col.id] = deepCopy(col.default_value);
                this.defaultValueService.cleanDefaultComponents(col).subscribe((defaultValue => {
                    if (isComponent(defaultValue) || isComponentArray(defaultValue)) {
                        this.defaultComponentValueDict[col.id] = defaultValue;
                    } else if (defaultValue === null) {
                        this.defaultComponentValueDict[col.id] = null;
                    }
                    if (this.relatedCtIds?.length && Object.keys(this.ctCtDict)?.length) {
                        //If component values, filter etc have already been loaded then reset
                        this._resetComponents();
                    }
                }))
            } else if ((col.default_value || col.default_value === 0) && isConstantValue(col.default_value)) {
                this.defaultValueDict[col.id] = col.default_value;
            }
        })
    }

    private get isEditing(): boolean {
        return !!this.component?.id;
    }

    private computeMissingPermissions(): void {
        this.recomputePermissionFlags();
        this.missingPermissions = this.buildPermissionMessages();
    }

    private recomputePermissionFlags(): void {
        this.canSaveComponent = this.isEditing
            ? this.userService.canEditExistingComponent(this.api_base)
            : this.userService.canCreateNewComponent(this.api_base);
        this.canSaveConstants = this.isEditing
            ? this.userService.canEditComponentConstant()
            : this.userService.canCreateComponentConstant();
        // Editing an existing link is a delete-old + create-new operation, so it needs all three
        // component_component permissions. Creating a new component only adds links, so it needs create only.
        this.canSaveLinks = this.isEditing
            ? this.userService.canLinkComponents()
                && this.userService.canCreateComponentLink()
                && this.userService.canUnlinkComponent()
            : this.userService.canCreateComponentLink();
    }

    private buildPermissionMessages(): string[] {
        const isEditing = this.isEditing;
        const prefix = 'You do not have permission to ';

        // Component perm is the umbrella: without it, nothing in the form is editable,
        // so the per-field messages would be redundant — show only the component message.
        if (!this.canSaveComponent) {
            return [prefix + (isEditing ? 'edit existing components.' : 'create new components.')];
        }
        const messages: string[] = [];
        const hasNonFileConstant = this.config.selected_cols.some(c =>
            c.type === 'constant_property' && this.cpDict[c.id]?.attributes.data_type !== 'file');
        if (hasNonFileConstant && !this.canSaveConstants) {
            messages.push(prefix + (isEditing ? 'edit property values.' : 'add new property values.'));
        }
        if (this.relatedCtIds?.length && !this.canSaveLinks) {
            messages.push(prefix + (isEditing ? 'edit component links.' : 'create component links.'));
        }
        return messages;
    }

    canEditConstantField(propId?: ModelID): boolean {
        // File fields manage their own upload/delete permissions via component_file feature flags
        // and don't flow through saveComponentConstants(), so the constant-write gate doesn't apply.
        if (propId && this.cpDict[propId]?.attributes.data_type === 'file') {
            return this.canSaveComponent;
        }
        return this.canSaveComponent && this.canSaveConstants;
    }

    canEditLinkField(): boolean {
        return this.canSaveComponent && this.canSaveLinks;
    }

    private _columnBlocks(c: ConfigColumn): boolean {
        // A column blocks save only when a write the user lacks permission for would actually be
        // issued. Edit mode skips disabled constant/link writes, so an already-populated required
        // value isn't a pending write. In create mode, a defaulted column only blocks if the value
        // would actually be written.
        if (this.isRequired(c)) {
            return !this.isEditing || !this._hasExistingValue(c);
        }
        if (this.isEditing) return false;
        if (c.type === 'component_type') {
            // cleanDefaultComponents may resolve a raw default to null (e.g. referenced component
            // was deleted); the resulting newSelectedComponentDict is what saveLinkedComponents
            // actually writes from.
            return !!this.newSelectedComponentDict[c.id]?.length;
        }
        return this._isWritableDefault(c.default_value);
    }

    private _hasExistingValue(c: ConfigColumn): boolean {
        if (c.type === 'constant_property') {
            const v = this.custom_constant_dict?.[c.id];
            return v === 0 || (v !== null && v !== undefined && v !== '');
        }
        const v = this.componentComponentDict?.[c.id] as unknown;
        return Array.isArray(v) ? v.length > 0 : !!v;
    }

    private _isWritableDefault(dv: unknown): boolean {
        if (dv === null || dv === undefined) return false;
        if (Array.isArray(dv)) return dv.length > 0;
        return true;
    }

    isSaveBlocked(): boolean {
        if (!this.canSaveComponent) return true;
        if (this.canSaveConstants && this.canSaveLinks) return false;
        return this.config.selected_cols.some(c => {
            if (c.type === 'constant_property' && this.cpDict[c.id]?.attributes.data_type !== 'file' && !this.canSaveConstants) {
                return this._columnBlocks(c);
            }
            if (c.type === 'component_type' && !this.canSaveLinks) return this._columnBlocks(c);
            return false;
        });
    }
}
